import { EvaluationResult } from '@evidentid/tprm-portal-lib/models/entity-details/EvaluationResult.model';
import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementType';

export interface ActionDocumentRequest {
    verificationId: string;
    requirementType: TprmRequirementType;
    instanceId?: string | null;
}

type DocumentableResult = EvaluationResult & { verificationId: string, coverageType: TprmRequirementType };

type MaybeResults = EvaluationResult[] | null | undefined;

function isDocumentable(result: EvaluationResult): result is DocumentableResult {
    return Boolean(result.verificationId && result.coverageType);
}

// An unparseable timestamp sorts last so a bad value can never win as the earliest.
function evaluatedAtMillis(result: EvaluationResult): number {
    const millis = Date.parse(result.evaluatedAt);
    return Number.isNaN(millis) ? Number.POSITIVE_INFINITY : millis;
}

function compareByEvaluatedAt(a: EvaluationResult, b: EvaluationResult): number {
    const aMillis = evaluatedAtMillis(a);
    const bMillis = evaluatedAtMillis(b);
    if (aMillis !== bMillis) {
        return aMillis < bMillis ? -1 : 1;
    }
    return a.insuredId.localeCompare(b.insuredId) || a.id.localeCompare(b.id);
}

/** Number of distinct entities a global action currently targets. */
export function getAffectedEntityCount(results: MaybeResults): number {
    return new Set((results ?? []).map((result) => result.insuredId)).size;
}

/** The entity whose document represents the action: earliest evaluation among entities that have a document. */
export function getRepresentativeEntityId(results: MaybeResults): string | null {
    const [ earliest ] = (results ?? []).filter(isDocumentable).sort(compareByEvaluatedAt);
    return earliest ? earliest.insuredId : null;
}

/** Unique document requests for every result of the representative entity. */
export function getRepresentativeDocumentRequests(results: MaybeResults): ActionDocumentRequest[] {
    const entityId = getRepresentativeEntityId(results);
    if (entityId === null) {
        return [];
    }
    const requests: ActionDocumentRequest[] = [];
    (results ?? [])
        .filter((result) => result.insuredId === entityId)
        .filter(isDocumentable)
        .forEach((result) => {
            const alreadyListed = requests.some((request) =>
                request.verificationId === result.verificationId &&
                request.requirementType === result.coverageType &&
                (request.instanceId ?? null) === (result.instanceId ?? null),
            );
            if (!alreadyListed) {
                requests.push({
                    verificationId: result.verificationId,
                    requirementType: result.coverageType,
                    instanceId: result.instanceId,
                });
            }
        });
    return requests;
}
